import Card from '@/components/Card';
import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  CheckCircleFilled,
  InboxOutlined,
  LinkedinOutlined,
} from '@ant-design/icons';
import { createFileRoute, Link } from '@tanstack/react-router';
import { Alert, Button, message, notification, type UploadProps } from 'antd';
import Dragger from 'antd/es/upload/Dragger';
import { useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useProfileOnboarding } from '@/context/OnboarrdingContext';
import clsx from 'clsx';
import type { RcFile } from 'antd/lib/upload';

export const Route = createFileRoute('/import-profile/$method')({
  component: RouteComponent,
});

/**
 * This route is used to import CV from existing PDF and eventualy provide help to import from Linkedin
 * @returns
 */
function RouteComponent() {
  const { method } = Route.useParams();
  const { t } = useTranslation();
  const { parseCV, isLoading, currentMessage } = useProfileOnboarding();
  const [selectedFile, setSelectedFile] = useState<RcFile | null>(null);

  const isLinkedin = method === 'linkedin';
  const translationKey = isLinkedin ? 'import-profile-linkedin' : 'import-profile-existing';

  const uploadProps: UploadProps = useMemo(() => {
    return {
      name: 'file',
      multiple: false,
      disabled: isLoading,
      customRequest: ({ onSuccess }) => {
        if (onSuccess) onSuccess('ok');
      },
      accept: '.pdf',
      onChange(info) {
        if (info.file.status === 'done' && info.file.originFileObj) {
          setSelectedFile(info.file.originFileObj);
        }
      },
      beforeUpload: file => {
        const isPDF = file.type === 'application/pdf';
        if (!isPDF) {
          message.error('You can only upload PDF files!');
        }
        return isPDF;
      },
    };
  }, [isLoading]);

  const handleContinue = async () => {
    if (!selectedFile) {
      notification.error({
        message: 'Error',
        description: 'Please select a file before continuing',
      });
      return;
    }

    parseCV(selectedFile);
  };

  return (
    <div className="h-screen w-full flex flex-col gap-8 items-center justify-center overflow-y-auto p-4">
      <header className="text-center">
        <h1 className="h1 mb-2">{t(`${translationKey}.title`)}</h1>
        <p className="body-1 text-gray">{t(`${translationKey}.description`)}</p>
      </header>
      <main className="flex flex-col gap-2 items-stretch w-full max-w-[450px]">
        {isLinkedin && !selectedFile && <LinkedinStepsHelper />}
        <Dragger {...uploadProps} className="drag-white">
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="subtitle-1">{t(`${translationKey}.upload_title`)}</p>
          <p
            className={clsx(
              'body-2 text-gray',
              selectedFile && 'text-primary flex items-center gap-2 justify-center'
            )}
          >
            {selectedFile ? (
              <>
                {`${selectedFile.name}`}
                <CheckCircleFilled />
              </>
            ) : (
              t(`${translationKey}.upload_description`)
            )}
          </p>
        </Dragger>
        {isLoading && (
          <Alert message="Processing your CV" description={currentMessage} type="info" showIcon />
        )}
        <Button
          type="primary"
          icon={<ArrowRightOutlined />}
          iconPosition="end"
          size="large"
          className="w-full"
          onClick={handleContinue}
          loading={isLoading}
          disabled={isLoading}
        >
          {t('global.continue')}
        </Button>
        <Link to="/import-profile">
          <Button
            type="dashed"
            icon={<ArrowLeftOutlined />}
            iconPosition="start"
            size="large"
            className="w-full"
            disabled={isLoading}
          >
            {t('global.come-back')}
          </Button>
        </Link>
      </main>
    </div>
  );
}

function LinkedinStepsHelper() {
  const { t } = useTranslation();

  return (
    <>
      <Card
        title={
          <>
            <span>{t('import-profile-linkedin.login_title')}</span>{' '}
            <a
              className="text-primary hover:underline"
              href="https://www.linkedin.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              LinkedIn <LinkedinOutlined />
            </a>
          </>
        }
        description={t('import-profile-linkedin.login_description')}
        icon={<p>1</p>}
      />
      <Card
        title={t('import-profile-linkedin.download_title')}
        description={
          <Trans
            i18nKey="import-profile-linkedin.download_description"
            components={{ span: <span className="text-primary font-medium" /> }}
          />
        }
        icon={<p>2</p>}
      />
    </>
  );
}
